// React
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Components
import {
  MBSPaperCard,
  FormikDatePicker,
  FormikTextField,
} from "Common/Components";
import { Alert, Button, Collapse, Stack, Typography } from "@mui/material";
import PatientTable from "./PatientTable";

// Icons
import { PersonSearchOutlined, Search } from "@mui/icons-material";

// Formik
import { useFormik } from "formik";

// Validation
import { mbsSearchValidationSchema } from "Validation/MBSSearchValidation";
import { searchMbsPatients } from "API/Resource/mbsPatientSearch";
import {
  mapScannedHealthCardToSearch,
  defaultSearchValues,
  matchesCurrentSearch,
} from "./formHelpers";
import { normalizeDateOfBirthValue, isAllZeroPhin } from "Utils/Normalization";

const SearchPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);

  // Searching
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [searchValues, setSearchValues] = useState(defaultSearchValues);

  // Scan
  const [scannedCardData, setScannedCardData] = useState(null);
  const hasProcessedScannedCardRef = useRef(false);

  // Pagination keys for searching more results
  const [inSystemNextKey, setInSystemNextKey] = useState(null);
  const [guestNextKey, setGuestNextKey] = useState(null);

  const clearSearchResults = () => {
    setPatients([]);
    setInSystemNextKey(null);
    setGuestNextKey(null);
  };

  const runSearch = useCallback(
    async (values, nextKey = null) => {
      setHasSearched(true);
      setSearchError("");
      setIsSearching(true);

      try {
        const response = await searchMbsPatients({ ...values, nextKey });
        const nextItems = response?.items || [];

        const isPagination = nextKey != null;
        setPatients((previousPatients) =>
          isPagination ? [...previousPatients, ...nextItems] : nextItems
        );

        // Update pagination keys for next searches
        setInSystemNextKey(response?.nextKey?.inSystem || null);
        setGuestNextKey(response?.nextKey?.guest || null);
      } catch (error) {
        setSearchError(error.message || t("errors.genericError"));
      } finally {
        setIsSearching(false);
      }
    },
    [t]
  );

  const normalizeSearchFormValues = (values = {}) => ({
    lastName: values.lastName || "",
    healthCardNumber: values.healthCardNumber || "",
    dateOfBirth: normalizeDateOfBirthValue(values.dateOfBirth),
  });

  const handleExternalSearch = async (values) => {
    const nextValues = normalizeSearchFormValues(values);

    setSearchValues(nextValues);
    formik.setValues(nextValues);
    await runSearch(nextValues);
  };

  const formik = useFormik({
    initialValues: searchValues,
    validationSchema: mbsSearchValidationSchema(t),
    onSubmit: async (values) => {
      setSearchValues(values);
      clearSearchResults();
      await runSearch(values);
    },
  });
  const { setValues } = formik;

  const alertOnClose = () => {
    setSearchError("");
    setHasSearched(false);
  };

  const handleClearSearch = () => {
    clearSearchResults();
    setHasSearched(false);
    setSearchError("");
    setScannedCardData(null);
    setSearchValues(defaultSearchValues);
    formik.resetForm({ values: defaultSearchValues });
  };

  useEffect(() => {
    const scannedCard = location.state?.scannedCard;

    if (!scannedCard || hasProcessedScannedCardRef.current) {
      return;
    }

    hasProcessedScannedCardRef.current = true;

    const mappedSearch = mapScannedHealthCardToSearch(scannedCard);
    const nextValues = normalizeSearchFormValues(mappedSearch.formValues);

    // store all the data from the scanned card
    setScannedCardData(mappedSearch.scannedCard);
    setSearchValues(nextValues);
    setValues(nextValues, false);
    runSearch(nextValues);

    // clear the navigation state to prevent re-processing
    navigate(location.pathname, { replace: true, state: null });
  }, [
    location.pathname,
    location.state?.scannedCard,
    navigate,
    runSearch,
    setValues,
  ]);

  const handleSearchMore = async () => {
    await runSearch(searchValues, {
      inSystem: inSystemNextKey,
      guest: guestNextKey,
    });
  };

  const ClearSearchButton = ({ sx }) => {
    return (
      <Button
        type="button"
        variant="text"
        sx={sx}
        onClick={handleClearSearch}
        disabled={isSearching}
      >
        {t("otp.clear")}
      </Button>
    );
  };

  return (
    <MBSPaperCard
      icon={<PersonSearchOutlined />}
      title={t("mbsSearch.title")}
      subtitle={t("mbsSearch.subtitle")}
    >
      <Stack spacing={0.75} component="form" onSubmit={formik.handleSubmit}>
        <Stack
          spacing={{ xs: 2, md: 1 }}
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <FormikTextField
            formik={formik}
            name="lastName"
            field={{
              label: t("patientForm.lastNameLabel"),
              required: false,
              clearable: true,
              disabled: isSearching,
              sx: { flex: { md: 1 } },
            }}
          />
          <FormikTextField
            formik={formik}
            name="healthCardNumber"
            field={{
              label: t("patientForm.healthCardNumberLabel"),
              required: false,
              clearable: true,
              disabled: isSearching,
              sx: { width: { xs: "100%", md: "14rem" } },
              showHelperTextWithError: true,
              helperText: isAllZeroPhin(formik.values.healthCardNumber)
                ? t("mbsSearch.validation.allZerosHealthCard")
                : undefined,
            }}
          />
          <FormikDatePicker
            formik={formik}
            field={{
              id: "dateOfBirth",
              label: t("patientForm.dobLabel"),
              required: false,
              clearable: true,
              disabled: isSearching,
              sx: { width: { xs: "100%", md: "14rem" } },
              helperText:
                isAllZeroPhin(formik.values.healthCardNumber) &&
                !formik.values.dateOfBirth
                  ? t("mbsSearch.validation.healthCardRequiresDOB")
                  : undefined,
            }}
          />
          <ClearSearchButton
            sx={{ display: { xs: "none", md: "inline-flex" }, py: 2 }}
          />
        </Stack>
        <Collapse
          in={Boolean(formik.submitCount > 0 && formik.errors.atLeastOneField)}
        >
          <Typography variant="body2" color="error">
            {formik.errors.atLeastOneField}
          </Typography>
        </Collapse>
        <Stack
          spacing={2}
          direction={{ xs: "column", sm: "row" }}
          alignItems="center"
        >
          <Button
            type="submit"
            variant="contained"
            startIcon={<Search />}
            disabled={isSearching}
            sx={{ width: { xs: "100%", sm: "fit-content" }, minWidth: "10rem" }}
          >
            {t("mbsSearch.search")}
          </Button>
          <ClearSearchButton
            sx={{ display: { xs: "inline-flex", md: "none" } }}
          />
        </Stack>
      </Stack>
      <Stack>
        <Collapse in={Boolean(searchError)}>
          <Alert severity="error" onClose={alertOnClose}>
            {searchError}
          </Alert>
        </Collapse>

        <Collapse in={hasSearched && !searchError}>
          <PatientTable
            patients={patients}
            setPatients={setPatients}
            matchesCurrentSearch={(patient) =>
              matchesCurrentSearch(searchValues, patient)
            }
            loading={isSearching}
            handleSearch={handleExternalSearch}
            handleSearchMore={handleSearchMore}
            canSearchMore={Boolean(inSystemNextKey || guestNextKey)}
            scannedCardData={scannedCardData}
            searchValues={searchValues}
          />
        </Collapse>
      </Stack>
    </MBSPaperCard>
  );
};

export default SearchPage;
